<template lang="pug">
  .pho-date-picker(:data-test-id="PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER_WRAPPER")
    v-menu(
      v-model="isActive"
      nudge-right="0"
      transition="scale-transition"
      min-width="290px"
      min-height="390px"
      :offset-y="true"
      :disabled="disabled"
      :close-on-content-click="false"
    )
      template(v-slot:activator="{ on, attrs }")
        v-text-field(
          v-on="on"
          v-bind="attrs"
          :value="formattedValue"
          :label="label"
          :clearable="clearable"
          :dense="dense"
          :placeholder="placeholder"
          :outlined="outlined"
          :data-test-id="PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER"
          @click:clear="clear"
        )
      v-date-picker(
        v-model="lazyValue"
        ref="datePickerRef"
        :locale="$i18n.locale"
        :range="range"
        :min="minimumDate"
        :max="maximumDate"
        :selected-items-text="getSelectedItemsText(lazyValue)"
        :data-test-id="PhoDatePickerAutomatedTestIds.PHO_DATE_TIME_PICKER_MENU_INPUT_FILTER"
        @change="onChanged"
      )
      v-divider
      v-row.justify-center.pa-3.mb-3(v-if="customButtonEnabled")
        .mr-1(v-for="({ nameText, customRangeFn }, index) in customRanges" :key="index")
          v-btn(color="primary white--text" rounded  small @click="customRangeFn")
            | {{ nameText }}
</template>

<script>
import { useContext } from '@/compat';
import {
  startOfDay, subDays, endOfDay, subMonths, subWeeks,
} from 'date-fns';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import {
  addSeconds, addDays, formatDate, parseDate, compareDesc, defaultDateDisplayFormat, getDate,
} from '@/helpers/misc/date';
import { contentManagementListAutomatedTestIds } from '@/components/content/constants';
import { isNullOrEmpty } from '@/helpers/misc';
import { PhoDatePickerAutomatedTestIds } from './constants';

export default {
  name: 'PhoDatePicker',
  props: {
    value: {
      type: [String, Array],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    displayFormat: {
      type: String,
      default() {
        return defaultDateDisplayFormat;
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    customButtonEnabled: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    isPendingList: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const { $i18n, $lang } = useContext();
    const lazyValue = ref(props.range ? [] : undefined);
    const datePickerRef = ref(null);
    const minimumDate = ref(null);
    const maximumDate = ref(null);

    const getSelectedItemsText = selectedItems => {
      if (isNullOrEmpty(selectedItems)) {
        return '-';
      }

      return selectedItems.length > 1 ? $i18n.t($lang.date.TWO_DATES_SELECTED) : selectedItems[0];
    };

    watch(() => props.value, newValue => {
      lazyValue.value = props.range ? (newValue || []).map(date => formatDate(date)) : formatDate(newValue);
    }, { deep: true, immediate: true });

    const isActive = ref(false);

    const formattedValue = computed(() => {
      if (!props.range) {
        return formatDate(lazyValue.value, props.displayFormat);
      }

      return (lazyValue.value || []).map(date => formatDate(date, props.displayFormat)).join(' ~ ');
    });

    const onChanged = newValue => {
      if (newValue) {
        let newDate = newValue;

        if (Array.isArray(newDate) && compareDesc(newDate[0], newDate[1]) < 0) {
          newDate = newDate.reverse();
        }

        const value = props.range
          ? newDate.map((date, index) => ((index === newDate.length - 1 ? addSeconds(addDays(date, 1), -1)
            : parseDate(date)).toISOString())) : parseDate(newDate).toISOString();

        emit('input', value);
      }

      isActive.value = false;
    };

    const clear = () => {
      if (datePickerRef.value) {
        datePickerRef.value.tableDate = datePickerRef.value.current;
      }

      lazyValue.value = undefined;
      emit('input', undefined);
    };

    watch(() => props.isPendingList, () => {
      clear();
      minimumDate.value = props.isPendingList ? getDate() : null;
      maximumDate.value = !props.isPendingList ? getDate() : null;
    });

    const customRanges = computed(() => [
      {
        nameText: `3${$i18n.t($lang.date.DAY)}`,
        dataTestId: PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER_THREE_DAY_BUTTON,
        customRangeFn: () => {
          const customRangeValue = [(startOfDay(subDays(new Date(), 2))).toISOString(), (endOfDay(new Date())).toISOString()];
          emit('input', customRangeValue);

          isActive.value = false;
        },
      },
      {
        nameText: `1${$i18n.t($lang.date.WEEK)}`,
        dataTestId: PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER_ONE_WEEK_BUTTON,
        customRangeFn: () => {
          const customRangeValue = [(startOfDay(subWeeks(new Date(), 1))).toISOString(), (endOfDay(new Date())).toISOString()];
          emit('input', customRangeValue);

          isActive.value = false;
        },
      },
      {
        nameText: `1${$i18n.t($lang.date.MONTH)}`,
        dataTestId: PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER_ONE_MONTH_BUTTON,
        customRangeFn: () => {
          const customRangeValue = [(startOfDay(subMonths(new Date(), 1))).toISOString(), (endOfDay(new Date())).toISOString()];
          emit('input', customRangeValue);

          isActive.value = false;
        },
      },
      {
        nameText: `3${$i18n.t($lang.date.MONTH)}`,
        dataTestId: PhoDatePickerAutomatedTestIds.PHO_DATE_PICKER_THREE_MONTH_BUTTON,
        customRangeFn: () => {
          const customRangeValue = [(startOfDay(subMonths(new Date(), 3))).toISOString(), (endOfDay(new Date())).toISOString()];
          emit('input', customRangeValue);

          isActive.value = false;
        },
      },
    ]);

    onMounted(() => {
      minimumDate.value = props.isPendingList ? getDate() : null;
      maximumDate.value = !props.isPendingList ? getDate() : null;
    });

    return {
      lazyValue,
      formattedValue,
      isActive,
      onChanged,
      clear,
      customRanges,
      PhoDatePickerAutomatedTestIds,
      contentManagementListAutomatedTestIds,
      getSelectedItemsText,
      datePickerRef,
      minimumDate,
      maximumDate,
    };
  },
};
</script>
